// Centralized settings system for Sellaris
// Manages default settings (user preferences) and last-used settings (session persistence)

import { buyerProfiles, type BuyerProfile } from './buyerProfiles';
import { liveAvatars, type LiveAvatarConfig } from './liveAvatars';

// Types
export type SessionMode = 'video' | 'voice';
export type InputMode = 'voice' | 'text';
export type Theme = 'dark' | 'light';
export type Language = 'en' | 'ru';
export type SalesMethodology = 'spin' | 'meddpicc' | 'both';
// '3d' = free TalkingHead avatar, 'liveavatar' = photorealistic streaming avatar.
export type AvatarModeSetting = '3d' | 'liveavatar';

export interface FeedbackDisplaySettings {
  showScoreBreakdown: boolean;
  showSpinBreakdown: boolean;
  showMeddpicBreakdown: boolean;
  showObjectionBreakdown: boolean;
  showLaerBreakdown: boolean;
  showStrengths: boolean;
  showImprovements: boolean;
  showJudgeTip: boolean;
  showTranscript: boolean;
  showConversationStats: boolean;
  showFillerWords: boolean;
  showVisualPresentation: boolean;
  showRecording: boolean;
}

export interface SessionSettings {
  mode: SessionMode;
  inputMode: InputMode;
  avatarId: string;
  avatarMode: AvatarModeSetting;
  voiceId: string;
  pttEnabled: boolean;
  buyerVoiceEnabled: boolean;
  language: Language;
  salesMethodology: SalesMethodology;
}

export interface AppSettings {
  defaults: SessionSettings;
  last: SessionSettings;
  theme: Theme;
  feedbackDisplay: FeedbackDisplaySettings;
}

// Available languages
export const LANGUAGES: { value: Language; label: string; flag: string }[] = [
  { value: 'en', label: 'English', flag: '🇺🇸' },
  { value: 'ru', label: 'Русский', flag: '🇷🇺' },
];

// LocalStorage keys
const STORAGE_KEYS = {
  defaultMode: 'bigeSalesCoach_settings.default.mode',
  defaultInputMode: 'bigeSalesCoach_settings.default.inputMode',
  defaultAvatar: 'bigeSalesCoach_settings.default.avatar',
  defaultAvatarMode: 'bigeSalesCoach_settings.default.avatarMode',
  defaultVoice: 'bigeSalesCoach_settings.default.voice',
  defaultPTT: 'bigeSalesCoach_settings.default.ptt',
  defaultBuyerVoice: 'bigeSalesCoach_settings.default.buyerVoice',
  defaultLanguage: 'bigeSalesCoach_settings.default.language',
  defaultMethodology: 'bigeSalesCoach_settings.default.methodology',
  lastMode: 'bigeSalesCoach_settings.last.mode',
  lastInputMode: 'bigeSalesCoach_settings.last.inputMode',
  lastAvatar: 'bigeSalesCoach_settings.last.avatar',
  lastAvatarMode: 'bigeSalesCoach_settings.last.avatarMode',
  lastVoice: 'bigeSalesCoach_settings.last.voice',
  lastPTT: 'bigeSalesCoach_settings.last.ptt',
  lastBuyerVoice: 'bigeSalesCoach_settings.last.buyerVoice',
  lastLanguage: 'bigeSalesCoach_settings.last.language',
  lastMethodology: 'bigeSalesCoach_settings.last.methodology',
  theme: 'bigeSalesCoach_settings.theme',
  feedbackDisplay: 'bigeSalesCoach_settings.feedbackDisplay',
  systemLanguage: 'bigeSalesCoach_settings.systemLanguage',
} as const;

// Default feedback display settings
const DEFAULT_FEEDBACK_DISPLAY: FeedbackDisplaySettings = {
  showScoreBreakdown: true,
  showSpinBreakdown: true,
  showMeddpicBreakdown: true,
  showObjectionBreakdown: true,
  showLaerBreakdown: true,
  showStrengths: true,
  showImprovements: true,
  showJudgeTip: true,
  showTranscript: true,
  showConversationStats: true,
  showFillerWords: true,
  showVisualPresentation: true,
  showRecording: true,
};

// Hard defaults (fallback if nothing is saved)
const HARD_DEFAULTS: SessionSettings = {
  mode: 'voice',
  inputMode: 'voice',
  // LiveAvatar IDs are workspace UUIDs, so there is no meaningful default —
  // the first entry of the live catalog wins once it loads.
  avatarId: '',
  // The LiveAvatar workspace is paid for, so video mode uses the photorealistic
  // avatar by default; users can still switch back to the free 3D one.
  avatarMode: 'liveavatar',
  voiceId: buyerProfiles[0]?.voiceId || '',
  pttEnabled: false,
  buyerVoiceEnabled: true,
  language: 'en',
  salesMethodology: 'spin',
};

/**
 * The video avatar mode was stored as 'heygen' before the move to LiveAvatar.
 * Nothing validates these values on read, so without this a returning user's
 * saved preference would silently become an unmatched mode.
 */
const parseAvatarMode = (value: string): AvatarModeSetting =>
  value === '3d' ? '3d' : 'liveavatar';

// Get a value from localStorage with fallback
const getStoredValue = <T>(key: string, fallback: T, parse?: (val: string) => T): T => {
  const stored = localStorage.getItem(key);
  if (stored === null) return fallback;
  if (parse) return parse(stored);
  return stored as unknown as T;
};

// Load session settings from localStorage
export const loadSessionSettings = (useLastFirst: boolean = true): SessionSettings => {
  // Priority: last > defaults > hard defaults
  const lastSettings: Partial<SessionSettings> = {
    mode: getStoredValue(STORAGE_KEYS.lastMode, undefined) as SessionMode | undefined,
    inputMode: getStoredValue(STORAGE_KEYS.lastInputMode, undefined) as InputMode | undefined,
    avatarId: getStoredValue(STORAGE_KEYS.lastAvatar, undefined),
    avatarMode: getStoredValue(STORAGE_KEYS.lastAvatarMode, undefined, parseAvatarMode) as AvatarModeSetting | undefined,
    voiceId: getStoredValue(STORAGE_KEYS.lastVoice, undefined),
    pttEnabled: getStoredValue(STORAGE_KEYS.lastPTT, undefined, v => v === 'true'),
    buyerVoiceEnabled: getStoredValue(STORAGE_KEYS.lastBuyerVoice, undefined, v => v !== 'false'),
    language: getStoredValue(STORAGE_KEYS.lastLanguage, undefined) as Language | undefined,
    salesMethodology: getStoredValue(STORAGE_KEYS.lastMethodology, undefined) as SalesMethodology | undefined,
  };

  const defaultSettings: SessionSettings = {
    mode: getStoredValue(STORAGE_KEYS.defaultMode, HARD_DEFAULTS.mode) as SessionMode,
    inputMode: getStoredValue(STORAGE_KEYS.defaultInputMode, HARD_DEFAULTS.inputMode) as InputMode,
    avatarId: getStoredValue(STORAGE_KEYS.defaultAvatar, HARD_DEFAULTS.avatarId),
    avatarMode: getStoredValue(STORAGE_KEYS.defaultAvatarMode, HARD_DEFAULTS.avatarMode, parseAvatarMode) as AvatarModeSetting,
    voiceId: getStoredValue(STORAGE_KEYS.defaultVoice, HARD_DEFAULTS.voiceId),
    pttEnabled: getStoredValue(STORAGE_KEYS.defaultPTT, HARD_DEFAULTS.pttEnabled, v => v === 'true'),
    buyerVoiceEnabled: getStoredValue(STORAGE_KEYS.defaultBuyerVoice, HARD_DEFAULTS.buyerVoiceEnabled, v => v !== 'false'),
    language: getStoredValue(STORAGE_KEYS.defaultLanguage, HARD_DEFAULTS.language) as Language,
    salesMethodology: getStoredValue(STORAGE_KEYS.defaultMethodology, HARD_DEFAULTS.salesMethodology) as SalesMethodology,
  };

  if (useLastFirst) {
    return {
      mode: lastSettings.mode ?? defaultSettings.mode,
      inputMode: lastSettings.inputMode ?? defaultSettings.inputMode,
      avatarId: lastSettings.avatarId ?? defaultSettings.avatarId,
      avatarMode: lastSettings.avatarMode ?? defaultSettings.avatarMode,
      voiceId: lastSettings.voiceId ?? defaultSettings.voiceId,
      pttEnabled: lastSettings.pttEnabled ?? defaultSettings.pttEnabled,
      buyerVoiceEnabled: lastSettings.buyerVoiceEnabled ?? defaultSettings.buyerVoiceEnabled,
      language: lastSettings.language ?? defaultSettings.language,
      salesMethodology: lastSettings.salesMethodology ?? defaultSettings.salesMethodology,
    };
  }

  return defaultSettings;
};

// Load only default settings (for Settings page)
export const loadDefaultSettings = (): SessionSettings => {
  return {
    mode: getStoredValue(STORAGE_KEYS.defaultMode, HARD_DEFAULTS.mode) as SessionMode,
    inputMode: getStoredValue(STORAGE_KEYS.defaultInputMode, HARD_DEFAULTS.inputMode) as InputMode,
    avatarId: getStoredValue(STORAGE_KEYS.defaultAvatar, HARD_DEFAULTS.avatarId),
    avatarMode: getStoredValue(STORAGE_KEYS.defaultAvatarMode, HARD_DEFAULTS.avatarMode, parseAvatarMode) as AvatarModeSetting,
    voiceId: getStoredValue(STORAGE_KEYS.defaultVoice, HARD_DEFAULTS.voiceId),
    pttEnabled: getStoredValue(STORAGE_KEYS.defaultPTT, HARD_DEFAULTS.pttEnabled, v => v === 'true'),
    buyerVoiceEnabled: getStoredValue(STORAGE_KEYS.defaultBuyerVoice, HARD_DEFAULTS.buyerVoiceEnabled, v => v !== 'false'),
    language: getStoredValue(STORAGE_KEYS.defaultLanguage, HARD_DEFAULTS.language) as Language,
    salesMethodology: getStoredValue(STORAGE_KEYS.defaultMethodology, HARD_DEFAULTS.salesMethodology) as SalesMethodology,
  };
};

// Save session settings to "last" storage (called during session)
export const saveLastSettings = (settings: Partial<SessionSettings>) => {
  if (settings.mode !== undefined) {
    localStorage.setItem(STORAGE_KEYS.lastMode, settings.mode);
  }
  if (settings.inputMode !== undefined) {
    localStorage.setItem(STORAGE_KEYS.lastInputMode, settings.inputMode);
  }
  if (settings.avatarId !== undefined) {
    localStorage.setItem(STORAGE_KEYS.lastAvatar, settings.avatarId);
  }
  if (settings.avatarMode !== undefined) {
    localStorage.setItem(STORAGE_KEYS.lastAvatarMode, settings.avatarMode);
  }
  if (settings.voiceId !== undefined) {
    localStorage.setItem(STORAGE_KEYS.lastVoice, settings.voiceId);
  }
  if (settings.pttEnabled !== undefined) {
    localStorage.setItem(STORAGE_KEYS.lastPTT, String(settings.pttEnabled));
  }
  if (settings.buyerVoiceEnabled !== undefined) {
    localStorage.setItem(STORAGE_KEYS.lastBuyerVoice, String(settings.buyerVoiceEnabled));
  }
  if (settings.language !== undefined) {
    localStorage.setItem(STORAGE_KEYS.lastLanguage, settings.language);
  }
  if (settings.salesMethodology !== undefined) {
    localStorage.setItem(STORAGE_KEYS.lastMethodology, settings.salesMethodology);
  }
};

// Save default settings (called from Settings page)
// Also updates "last" settings so the next session uses the new defaults
export const saveDefaultSettings = (settings: Partial<SessionSettings>) => {
  if (settings.mode !== undefined) {
    localStorage.setItem(STORAGE_KEYS.defaultMode, settings.mode);
    localStorage.setItem(STORAGE_KEYS.lastMode, settings.mode);
  }
  if (settings.inputMode !== undefined) {
    localStorage.setItem(STORAGE_KEYS.defaultInputMode, settings.inputMode);
    localStorage.setItem(STORAGE_KEYS.lastInputMode, settings.inputMode);
  }
  if (settings.avatarId !== undefined) {
    localStorage.setItem(STORAGE_KEYS.defaultAvatar, settings.avatarId);
    localStorage.setItem(STORAGE_KEYS.lastAvatar, settings.avatarId);
  }
  if (settings.avatarMode !== undefined) {
    localStorage.setItem(STORAGE_KEYS.defaultAvatarMode, settings.avatarMode);
    localStorage.setItem(STORAGE_KEYS.lastAvatarMode, settings.avatarMode);
  }
  if (settings.voiceId !== undefined) {
    localStorage.setItem(STORAGE_KEYS.defaultVoice, settings.voiceId);
    localStorage.setItem(STORAGE_KEYS.lastVoice, settings.voiceId);
  }
  if (settings.pttEnabled !== undefined) {
    localStorage.setItem(STORAGE_KEYS.defaultPTT, String(settings.pttEnabled));
    localStorage.setItem(STORAGE_KEYS.lastPTT, String(settings.pttEnabled));
  }
  if (settings.buyerVoiceEnabled !== undefined) {
    localStorage.setItem(STORAGE_KEYS.defaultBuyerVoice, String(settings.buyerVoiceEnabled));
    localStorage.setItem(STORAGE_KEYS.lastBuyerVoice, String(settings.buyerVoiceEnabled));
  }
  if (settings.language !== undefined) {
    localStorage.setItem(STORAGE_KEYS.defaultLanguage, settings.language);
    localStorage.setItem(STORAGE_KEYS.lastLanguage, settings.language);
  }
  if (settings.salesMethodology !== undefined) {
    localStorage.setItem(STORAGE_KEYS.defaultMethodology, settings.salesMethodology);
    localStorage.setItem(STORAGE_KEYS.lastMethodology, settings.salesMethodology);
  }
};

/**
 * Get avatar by ID with fallback.
 *
 * Only resolves against the placeholder list, so it always returns the
 * placeholder in practice — real avatars live in the workspace catalog fetched
 * by `useLiveAvatarList`. Callers that can reach that catalog should resolve
 * against it (see `useAppSettings(availableAvatars)`) rather than calling this.
 */
export const getAvatarById = (id: string): LiveAvatarConfig => {
  return liveAvatars.find(a => a.id === id) || liveAvatars[0];
};

// Get buyer profile by voice ID with fallback
export const getBuyerProfileByVoiceId = (voiceId: string): BuyerProfile => {
  return buyerProfiles.find(p => p.voiceId === voiceId) || buyerProfiles[0];
};

// Get buyer profile by ID with fallback
export const getBuyerProfileById = (id: string): BuyerProfile => {
  return buyerProfiles.find(p => p.id === id) || buyerProfiles[0];
};

// Load theme preference
export const loadTheme = (): Theme => {
  const stored = getStoredValue(STORAGE_KEYS.theme, 'dark') as string;
  // Migrate 'system' to 'dark' for existing users
  if (stored === 'system') return 'dark';
  return stored as Theme;
};

// Save theme preference
export const saveTheme = (theme: Theme) => {
  localStorage.setItem(STORAGE_KEYS.theme, theme);
};

// Load feedback display settings
export const loadFeedbackDisplaySettings = (): FeedbackDisplaySettings => {
  // Locked: always return all-enabled defaults
  return { ...DEFAULT_FEEDBACK_DISPLAY };
};

// Save feedback display settings
export const saveFeedbackDisplaySettings = (settings: Partial<FeedbackDisplaySettings>) => {
  const current = loadFeedbackDisplaySettings();
  const updated = { ...current, ...settings };
  localStorage.setItem(STORAGE_KEYS.feedbackDisplay, JSON.stringify(updated));
};

// Load system language preference
export const loadSystemLanguage = (): Language => {
  return getStoredValue(STORAGE_KEYS.systemLanguage, 'en') as Language;
};

// Save system language preference
export const saveSystemLanguage = (language: Language) => {
  localStorage.setItem(STORAGE_KEYS.systemLanguage, language);
};
